const GEMINI_URL = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-3.1-flash-lite-preview:generateContent?key=AIzaSyC0Y2BcOff1cLeSxuU0jlXFbiPfGraZfJc'

const SYSTEM_PROMPT = `You are an AI dental assistant for a dental clinic directory in Georgia (Tbilisi). Help users find the best dental clinics, services, and answer dental health questions.

IMPORTANT: When recommending clinics, you MUST include a JSON block at the end of your response with the clinic IDs you recommend. Format:
<!--CLINICS:["clinic-id-1","clinic-id-2"]-->

Only use IDs from this list. Be helpful, concise. Respond in the same language the user writes in (Georgian or English).`

export const askGemini = async (thread, clinics) => {
  const clinicContext = clinics.map(c =>
    `ID: ${c.id} | ${c.name} (${c.district}) - Rating: ${c.rating}, Services: ${c.services.join(', ')}, Hours: ${c.hours}, Price: ${c.priceRange}`
  ).join('\n')

  const contents = thread.map(m => ({
    role: m.role === 'assistant' ? 'model' : 'user',
    parts: [{ text: m.content }]
  }))

  const res = await fetch(GEMINI_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      systemInstruction: { parts: [{ text: SYSTEM_PROMPT + '\n\nAvailable clinics:\n' + clinicContext }] },
      contents,
      generationConfig: { temperature: 0.3, maxOutputTokens: 1024 }
    })
  })
  const data = await res.json()
  return data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response received.'
}

const parseClinicIds = (text) => {
  const match = text.match(/<!--CLINICS:\[([^\]]*)\]-->/)
  if (!match) return []
  try { return JSON.parse('[' + match[1] + ']') } catch { return [] }
}

const cleanResponse = (text) => (text || '').replace(/<!--CLINICS:\[[^\]]*\]-->/, '').trim()

const formatHtml = (text) => {
  if (!text) return ''
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\n/g, '<br>')
}

export const AiModal = {
  show: (el, s) => s.root.showAiModal,
  position: 'fixed',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  zIndex: '1000',

  Backdrop: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    background: 'rgba(0,0,0,0.5)',
    backdropFilter: 'blur(4px)',
    cursor: 'pointer',
    onClick: (e, el, s) => s.root.update({ showAiModal: false })
  },

  ModalPanel: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    attr: { style: 'transform: translate(-50%, -50%)' },
    width: '90%',
    maxWidth: '640px',
    maxHeight: '80vh',
    background: '#FFFFFF',
    round: 'B',
    boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
    flow: 'y',
    overflow: 'hidden',

    ModalHeader: {
      flow: 'x',
      align: 'center space-between',
      padding: 'A B',
      borderBottom: '1px solid #E5E5EA',
      flexShrink: '0',

      HeaderLeft: {
        flow: 'x',
        align: 'center center',
        gap: 'Y',
        SparkleIcon: { extends: 'Icon', icon: 'sparkle', width: 'A', height: 'A', color: 'teal' },
        HeaderTitle: { text: '{{ aiAssistant | polyglot }}', fontSize: 'A', fontWeight: '700', color: '#1D1D1F' }
      },
      CloseBtn: {
        tag: 'button',
        text: '{{ u2715 | polyglot }}',
        background: 'transparent',
        border: 'none',
        fontSize: 'A',
        color: '#6E6E73',
        cursor: 'pointer',
        padding: 'Y',
        round: 'Y',
        ':hover': { background: '#F0F0F5' },
        onClick: (e, el, s) => s.root.update({ showAiModal: false })
      }
    },

    ThreadBody: {
      flow: 'y',
      gap: 'Z',
      padding: 'A B',
      overflow: 'auto',
      flex: '1',

      children: (el, s) => {
        const thread = s.root.aiModalThread || []
        const clinics = s.root.clinics || []
        return thread.map(m => {
          const entry = { role: m.role, content: m.content }
          if (m.role === 'assistant') {
            const ids = parseClinicIds(m.content)
            if (ids.length) {
              entry.recommendedClinics = ids.map(id => clinics.find(c => c.id === id)).filter(Boolean)
            }
            entry.cleanContent = cleanResponse(m.content)
          }
          return entry
        })
      },
      childrenAs: 'state',
      childExtends: {
        flow: 'y',
        gap: 'Z',
        alignSelf: (el, s) => s.role === 'user' ? 'flex-end' : 'flex-start',
        maxWidth: '90%',

        MessageBubble: {
          padding: 'Z A',
          round: 'A',
          background: (el, s) => s.role === 'user' ? '#0D1117' : '#F0F0F5',

          MessageText: {
            html: (el, s) => formatHtml(s.role === 'user' ? s.content : (s.cleanContent || s.content)),
            fontSize: 'Z',
            color: (el, s) => s.role === 'user' ? '#FFFFFF' : '#1D1D1F',
            lineHeight: '1.6'
          }
        },

        ClinicCards: {
          show: (el, s) => s.recommendedClinics && s.recommendedClinics.length > 0,
          flow: 'y',
          gap: 'Y',
          width: '100%',

          children: (el, s) => s.recommendedClinics || [],
          childrenAs: 'state',
          childExtends: {
            extends: 'Link',
            href: (el, s) => '/clinic?id=' + s.id,
            textDecoration: 'none',
            color: 'inherit',
            flow: 'x',
            gap: 'Z',
            align: 'center flex-start',
            padding: 'Z A',
            round: 'Z',
            background: '#FFFFFF',
            border: '1px solid #E5E5EA',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            ':hover': { border: '1px solid #00C9A7', boxShadow: '0 4px 12px rgba(0,201,167,0.1)' },

            CardLogo: {
              tag: 'img',
              width: 'B1',
              height: 'B1',
              round: 'Y',
              flexShrink: '0',
              attr: {
                src: (el, s) => s.logo || 'https://ui-avatars.com/api/?name=' + encodeURIComponent(s.name) + '&background=0D1117&color=00C9A7&size=96&bold=true&format=svg'
              }
            },
            CardInfo: {
              flow: 'y',
              gap: 'W',
              flex: '1',
              minWidth: '0',
              CardName: { text: (el, s) => s.name, fontSize: 'Z', fontWeight: '600', color: '#1D1D1F' },
              CardMeta: {
                flow: 'x',
                gap: 'Y',
                align: 'center flex-start',
                CardDistrict: { text: (el, s) => s.district, fontSize: 'Y', color: '#6E6E73' },
                CardDot: { text: '{{ u00b7 | polyglot }}', fontSize: 'Y', color: '#6E6E73' },
                CardPrice: { text: (el, s) => s.priceRange, fontSize: 'Y', color: '#6E6E73' }
              }
            },
            CardRating: {
              flow: 'x',
              gap: 'W',
              align: 'center center',
              flexShrink: '0',
              RatingStar: { extends: 'Icon', icon: 'star', width: 'Z', height: 'Z', color: 'teal' },
              RatingVal: { text: (el, s) => String(s.rating), fontSize: 'Z', fontWeight: '600', color: 'teal' }
            }
          }
        }
      },

      LoadingIndicator: {
        show: (el, s) => s.root.aiModalLoading,
        flow: 'x',
        align: 'flex-start center',
        gap: 'Y',
        padding: 'Z A',
        round: 'A',
        background: '#F0F0F5',
        alignSelf: 'flex-start',
        LoadingDots: {
          text: '{{ thinking | polyglot }}',
          fontSize: 'Z',
          color: 'teal',
          fontWeight: '500'
        }
      }
    },

    ModalFooter: {
      flow: 'x',
      gap: 'Z',
      padding: 'A B',
      borderTop: '1px solid #E5E5EA',
      align: 'center center',
      flexShrink: '0',

      FollowUpInput: {
        tag: 'input',
        type: 'text',
        placeholder: '{{ followUpPlaceholder | polyglot }}',
        flex: '1',
        padding: 'Z A',
        round: 'A',
        background: '#F0F0F5',
        border: '1px solid #E5E5EA',
        fontSize: 'Z',
        color: '#1D1D1F',
        outline: 'none',
        ':focus': { borderColor: '#00C9A7' },
        on: {
          keydown: (e, el, s) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              el.parent.FollowUpBtn.node.click()
            }
          }
        }
      },
      FollowUpBtn: {
        tag: 'button',
        background: '#0D1117',
        color: '#FFFFFF',
        border: 'none',
        padding: 'Z A',
        round: 'A',
        fontSize: 'Z',
        fontWeight: '600',
        cursor: 'pointer',
        ':hover': { opacity: '0.9' },
        align: 'center center',
        SendIcon: { extends: 'Icon', icon: 'send', width: 'Z', height: 'Z', color: '#FFFFFF' },
        onClick: async (e, el, s) => {
          const input = el.lookup('FollowUpInput')
          const msg = input.node.value
          if (!msg.trim() || s.root.aiModalLoading) return
          input.node.value = ''
          const thread = [...(s.root.aiModalThread || []), { role: 'user', content: msg }]
          s.root.update({ aiModalThread: thread, aiModalLoading: true })
          try {
            const reply = await askGemini(thread, s.root.clinics || [])
            s.root.update({ aiModalThread: [...thread, { role: 'assistant', content: reply }], aiModalLoading: false })
          } catch (err) {
            s.root.update({ aiModalThread: [...thread, { role: 'assistant', content: 'Error: ' + err.message }], aiModalLoading: false })
          }
        }
      }
    }
  }
}
